import { TCSSMode, TEditorFramework } from './types';

// ---- HTML starter templates (per framework) ----

export const STARTER_HTML: Record<TEditorFramework, string> = {
  vanilla: `<div id="app">
  <h1>Hello, World!</h1>
  <p>Start building something amazing.</p>
</div>`,

  react: `<div id="root"></div>`,

  vue: `<div id="app">
  <h1>{{ message }}</h1>
  <button @click="count++">Count: {{ count }}</button>
</div>`,

  typescript: `<div id="app">
  <h1>Hello, TypeScript!</h1>
  <p id="output"></p>
</div>`,
};

// ---- CSS starter templates (per CSS mode) ----

export const STARTER_CSS: Record<TCSSMode, string> = {
  css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: system-ui, -apple-system, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
}

#app {
  text-align: center;
}

h1 {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}`,

  scss: `$primary: #7c3aed;
$bg: #0f172a;
$text: #e2e8f0;

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: system-ui, -apple-system, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: $bg;
  color: $text;
}

#app {
  text-align: center;

  h1 {
    font-size: 2rem;
    color: $primary;
    margin-bottom: 0.5rem;
  }
}`,

  postcss: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: system-ui, -apple-system, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
}

#app {
  text-align: center;

  & h1 {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  & p {
    color: #94a3b8;
  }
}`,

  modules: `/* CSS Modules: class names are auto-scoped.
   Use styles.className in JS to get scoped names.
   e.g. el.className = styles.container */

.container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
  font-family: system-ui, -apple-system, sans-serif;
}

.content {
  text-align: center;
}

.title {
  font-size: 2rem;
  color: #7c3aed;
  margin-bottom: 0.5rem;
}`,
};

// ---- JS starter templates (per framework) ----

export const STARTER_JS: Record<TEditorFramework, string> = {
  vanilla: `const app = document.getElementById('app');

console.log('Hello from JavaScript!');`,

  react: `const { useState } = React;

function App() {
  const [count, setCount] = useState(0);

  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <h1>React App</h1>
      <p>Count: {count}</p>
      <button onClick={() => setCount(c => c + 1)}>
        Increment
      </button>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);`,

  vue: `const { createApp, ref } = Vue;

createApp({
  setup() {
    const message = ref('Hello from Vue!');
    const count = ref(0);
    return { message, count };
  }
}).mount('#app');`,

  typescript: `interface AppState {
  message: string;
  count: number;
}

const state: AppState = {
  message: 'Hello, TypeScript!',
  count: 0,
};

const output = document.getElementById('output') as HTMLElement;
output.textContent = \`\${state.message} (count: \${state.count})\`;`,
};

// ---- Practice problem templates ----

export const getPracticeTemplate = (
  _framework: TEditorFramework,
  functionName: string,
  inputNames: string[]
): string => {
  return `function ${functionName}(${inputNames.join(', ')}) {\n // Write your code below\n}`;
};

// ---- Composite getter ----

export interface StarterTemplate {
  html: string;
  css: string;
  js: string;
}

export const getStarterTemplate = (
  framework: TEditorFramework,
  cssMode: TCSSMode
): StarterTemplate => ({
  html: STARTER_HTML[framework],
  css: STARTER_CSS[cssMode],
  js: STARTER_JS[framework],
});

// ---- Base template detection ----

export const isBaseTemplate = (currentCode: string, templateCode: string): boolean => {
  return currentCode.trim() === templateCode.trim();
};

// ---- Human-readable labels ----

export const FRAMEWORK_LABELS: Record<TEditorFramework, string> = {
  vanilla: 'JavaScript',
  react: 'React',
  vue: 'Vue',
  typescript: 'TypeScript',
};

export const CSS_MODE_LABELS: Record<TCSSMode, string> = {
  css: 'CSS',
  scss: 'SCSS',
  postcss: 'PostCSS',
  modules: 'CSS Modules',
};
